import { useMemo } from 'react';

import { ParameterNode } from '@data';
import { useRelations, useUiParameters, useUiGraphLine } from '@queries';

/**
 *
 * @param graphId id of a graph node
 * @returns `Record<Position, Record<Axis, ParameterNode>>` for the current graph.
 *
 * It allows us to quick access the nodes we will use in parameter selector.
 */
export const useGraphParameterMap = (graphId: string) => {
    const { data: relations = [] } = useRelations();
    const { data: graphLines = [] } = useUiGraphLine();
    const graphLineMap = useMemo(
        () => Object.fromEntries(graphLines.map((lineNode) => [lineNode.id, lineNode] as const)),
        [graphLines],
    );

    const { data: parameters = [] } = useUiParameters();

    /**
     * We find all graph lines for the current graph,
     * by using a relation and getting the correct graphLine from graphLineMap.
     *
     * Graph Line has a timeSeriesId which can be used to find parameter nodes for this particular graph line.
     */
    const timeSeriesIds = useMemo(() => {
        return relations
            .filter((relation) => relation.parentUiNodeId === graphId)
            .map((relation) => {
                return graphLineMap[relation.childUiNodeId]?.timeSeries.id;
            });
    }, [graphId, graphLineMap, relations]);

    const graphParameters = useMemo(() => {
        return parameters.filter(
            (parameter) => parameter.parameter.timeSeriesId && timeSeriesIds.includes(parameter.parameter.timeSeriesId),
        );
    }, [parameters, timeSeriesIds]);

    /**
     * We use graph parameter list, since there can be up to 4 parameters per position and up to 10 positions.
     * We can't use a map here.
     *
     * This map is useful for accessing data without looping through parameter list.
     */
    return useMemo(() => {
        return graphParameters.reduce(
            (acc, parameterNode) => {
                const {
                    parameter: { position, axis },
                } = parameterNode;
                if (!position) {
                    return acc;
                }

                if (!acc[position]) {
                    acc[position] = {};
                }

                acc[position][axis || 'x'] = parameterNode;

                return acc;
            },
            {} as Record<string, Record<string, ParameterNode>>,
        );
    }, [graphParameters]);
};
